/**
 * consent-unblock
 * -------------------------------------------------------------------------------------------------
 * Releases `type="javascript/blocked"` scripts the moment the visitor consents, instead of waiting
 * for Pandectes to do it.
 *
 * WHY THIS EXISTS
 * Measured on lelafine.de on 7 August 2026: after a real Accept click, Shopify's Customer Privacy
 * API correctly reports `marketingAllowed() === true`, but all 11 `javascript/blocked` tags stay
 * blocked — on that page view and on every subsequent one. Klaviyo's `klaviyo.init()` and the
 * Trustpilot widget therefore never run at all.
 *
 * That is why the welcome popup felt so slow: it was not the form's own delay. Klaviyo's modern
 * `klaviyo.js` does load unblocked and self-initialises, so a form eventually appears on a later
 * page view, but the theme's init and everything else gated this way never fire.
 *
 * Consent is still required first. Nothing here loosens the gate — it only closes it faster once
 * the visitor has actually said yes. A script whose category the visitor declined is left blocked.
 *
 * See docs/consent.md.
 */
(() => {
  const BLOCKED_TYPE = 'javascript/blocked';

  /**
   * Pandectes numbers its categories; the theme's own two tags use words. Both appear on the page,
   * which is itself worth knowing — a mismatch is an easy way to gate something by accident.
   *
   * 1 strictly necessary · 2 functionality/preferences · 3 performance/analytics · 4 targeting
   */
  const KATEGORI = {
    '1': 'necessary',
    '2': 'preferences',
    '3': 'analytics',
    '4': 'marketing',
    necessary: 'necessary',
    functionality: 'preferences',
    preferences: 'preferences',
    performance: 'analytics',
    analytics: 'analytics',
    targeting: 'marketing',
    marketing: 'marketing',
  };

  function erKlaviyoFormularmotor(script) {
    const src = script?.getAttribute?.('src') || '';
    // Klaviyo currently has two storefront loader URLs. The app embed uses static.klaviyo.com,
    // while the consent-gated theme loader uses a.klaviyo.com. Both must be recognised here;
    // otherwise the script we actually release after an Accept click sits behind lower-value
    // marketing downloads and never receives the high network priority below.
    return /\/\/(?:static\.klaviyo\.com\/onsite\/js\/|a\.klaviyo\.com\/media\/js\/onsite\/onsite\.js(?:[?#]|$))/i.test(src);
  }

  function tilladt(kategori) {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return false;
    const k = KATEGORI[String(kategori || '').trim().toLowerCase()];
    try {
      switch (k) {
        case 'necessary':
          return true;
        case 'preferences':
          return cp.preferencesProcessingAllowed ? cp.preferencesProcessingAllowed() : cp.preferencesAllowed?.();
        case 'analytics':
          return cp.analyticsProcessingAllowed?.();
        case 'marketing':
          return cp.marketingAllowed?.();
        default:
          // Ukendt kategori: fris aldrig. Hellere et script der ikke koerer end et samtykke vi ikke har.
          return false;
      }
    } catch {
      return false;
    }
  }

  /**
   * A script tag already in the DOM will not execute just because its `type` changes. It has to be
   * replaced with a fresh node, which is why every attribute is copied across.
   */
  function frigiv(gammel) {
    const ny = document.createElement('script');
    for (const a of gammel.attributes) {
      if (a.name === 'type') continue;
      ny.setAttribute(a.name, a.value);
    }
    ny.setAttribute('data-consent-unblocked', '1');
    if (gammel.src) {
      ny.async = gammel.async;
      ny.defer = gammel.defer;
      // The existing Klaviyo form remains consent-gated. Once marketing consent is actually
      // granted, however, its targeting result owns the PDP offer row and should not sit behind
      // lower-value marketing downloads on a constrained iPhone connection.
      if (erKlaviyoFormularmotor(gammel)) ny.setAttribute('fetchpriority', 'high');
    } else {
      ny.textContent = gammel.textContent;
    }
    gammel.parentNode.insertBefore(ny, gammel.nextSibling);
    gammel.remove();
    return ny;
  }

  let kørt = 0;
  let hurtigFrigivelseTimer = null;

  function frigivAlle(anledning) {
    const blokerede = [...document.querySelectorAll(`script[type="${BLOCKED_TYPE}"]`)]
      .map((script, index) => ({ script, index }))
      .sort((a, b) => {
        const priorityA = erKlaviyoFormularmotor(a.script) ? 0 : 1;
        const priorityB = erKlaviyoFormularmotor(b.script) ? 0 : 1;
        return priorityA - priorityB || a.index - b.index;
      })
      .map(({ script }) => script);
    if (!blokerede.length) return 0;

    let n = 0;
    for (const s of blokerede) {
      const kat = s.dataset.cookiecategory;
      if (!tilladt(kat)) continue;
      try {
        frigiv(s);
        n++;
      } catch (e) {
        /* et enkelt script maa ikke vaelte resten */
      }
    }
    if (n) {
      kørt += n;
      // Klaviyo booter paa sit eget init; giv den et hint om at der nu er samtykke, saa formularen
      // ikke venter paa naeste sidevisning.
      try {
        window.klaviyo = window.klaviyo || [];
        if (typeof window.klaviyo.push === 'function') window.klaviyo.push(['identify', {}]);
      } catch {}
      document.dispatchEvent(new CustomEvent('consent:scripts-unblocked', { detail: { antal: n, anledning } }));
    }
    return n;
  }

  /**
   * Shopify can emit visitorConsentCollected a fraction before customerPrivacy reflects the new
   * value on a constrained iPhone. The one-second safety pulse below is deliberately cheap, but
   * it is too coarse for the welcome offer's first mobile render. Recheck locally for two seconds;
   * every attempt still passes through tilladt(), so nothing runs before real marketing consent.
   */
  function startHurtigFrigivelse(anledning) {
    window.clearInterval(hurtigFrigivelseTimer);
    let forsøg = 0;
    hurtigFrigivelseTimer = window.setInterval(() => {
      forsøg++;
      frigivAlle(`${anledning}-hurtig-${forsøg}`);
      const tilbage = document.querySelectorAll(`script[type="${BLOCKED_TYPE}"]`).length;
      if (!tilbage || forsøg >= 40) {
        window.clearInterval(hurtigFrigivelseTimer);
        hurtigFrigivelseTimer = null;
      }
    }, 50);
  }

  // Shopify fyrer denne, naar samtykket indsamles , det er det tidligste paalidelige signal.
  document.addEventListener('visitorConsentCollected', () => {
    frigivAlle('visitorConsentCollected');
    startHurtigFrigivelse('visitorConsentCollected');
  });

  // Pandectes' egen event, hvis den findes.
  ['pandectes:consent', 'PandectesConsentUpdated', 'cookie_consent_update'].forEach((navn) => {
    window.addEventListener(navn, () => {
      frigivAlle(navn);
      startHurtigFrigivelse(navn);
    });
  });

  // Ved load: samtykket kan allerede ligge i en cookie fra et tidligere besoeg.
  const start = () => frigivAlle('load');
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /**
   * Sidste net. Customer Privacy API'et er selv indlaest asynkront, saa ved foerste besoeg kan
   * `window.Shopify.customerPrivacy` mangle, naar de to ovenstaaende koerer. Vi prøver derfor i et
   * kort vindue og stopper, saa snart der ikke er mere at frigive.
   */
  let forsøg = 0;
  const puls = setInterval(() => {
    forsøg++;
    const tilbage = document.querySelectorAll(`script[type="${BLOCKED_TYPE}"]`).length;
    if (!tilbage || forsøg > 30) {
      clearInterval(puls);
      return;
    }
    frigivAlle('puls');
  }, 1000);

  // Til fejlsoegning i konsollen.
  window.__consentUnblock = { frigivAlle, tilladt, antalFrigivet: () => kørt };
})();
